import { useState, useEffect } from 'react';

/**
 * @hook useNetworkStatus
 * @description
 * Hook personalizado que detecta el estado de conexión a internet del usuario en tiempo real.
 * Retorna un booleano que indica si el usuario está en línea (`true`) o fuera de línea (`false`).
 *
 * Utiliza los eventos `online` y `offline` del navegador para mantenerse actualizado.
 *
 * @returns {boolean} `true` si el usuario está conectado, `false` si está desconectado.
 */
const useNetworkStatus = () => {
	// Estado inicial según si el navegador está en línea
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	useEffect(() => {
		/**
		 * @function handleOnline
		 * @description Se ejecuta cuando el navegador detecta que volvió la conexión.
		 */
		const handleOnline = () => {
			setIsOnline(true);
		};

		/**
		 * @function handleOffline
		 * @description Se ejecuta cuando el navegador detecta que se perdió la conexión.
		 */
		const handleOffline = () => {
			setIsOnline(false);
		};

		// Escuchar eventos de red
		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		// Cleanup de los listeners al desmontar el componente
		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	return isOnline;
};

export default useNetworkStatus;